// frontend/src/index.js
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-06-04
// Updated:      2026-09-17
//
// Description:
//   Entry point for React app. Loads global styles (Leaflet → Tailwind → App overrides),
//   captures the browser PWA install event, mounts App inside StrictMode,
//   registers the Vision service worker, and reports web vitals.

import React from "react";
import ReactDOM from "react-dom/client";

// ✅ Global styles (order matters)
// 1) Leaflet first so its defaults are present
import "leaflet/dist/leaflet.css";
// 2) Tailwind layers + theme variables
import "./index.css";
// 3) Your app-level overrides (intentionally kept)
import "./App.css";

import App from "./App";
import reportWebVitals from "./reportWebVitals";

// Capture the browser-provided PWA install event at application bootstrap.
// Layout may mount after the browser has completed its installability checks,
// so the event is retained here and surfaced to the authenticated shell.
if (typeof window !== "undefined") {
  window.__visionPwaInstallPrompt = null;

  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    window.__visionPwaInstallPrompt = event;

    window.dispatchEvent(
      new CustomEvent("vision:pwa-install-available")
    );
  });

  window.addEventListener("appinstalled", () => {
    window.__visionPwaInstallPrompt = null;

    window.dispatchEvent(
      new CustomEvent("vision:pwa-installed")
    );
  });
}

// NOTE: ThemeProvider is already applied inside App.js around the router/providers,
// so we avoid double-wrapping here.
const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

// PWA service-worker registration.
// Registration belongs at application bootstrap level rather than inside React
// so provider/router remounts cannot trigger duplicate registration attempts.
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/service-worker.js")
      .catch((error) => {
        console.error("Vision PWA service-worker registration failed:", error);
      });
  });
}
